import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { 
  ArrowLeft, Calculator, Printer, Check, AlertTriangle, 
  Euro, Percent, Loader2, History
} from "lucide-react";
import logoMcom from "@/assets/logo192.png";
import storage from "@/utils/storage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VOCalculatorPage() {
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [printing, setPrinting] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    vehicle_description: "",
    has_vat: true,  // true = avec TVA, false = sans TVA (TVA sur marge)
    purchase_price_ttc: "",  // Prix d'achat TTC
    reconditioning_cost: "",
    selling_price_ttc: "",
    has_financing: false,
    has_delivery: false,
    waxyol_type: "NON",  // NON, BASIQUE, CERAMIQUE
    has_warranty: false,
    accessories_amount: "",
    seller_name: "",
  });

  useEffect(() => {
    const parsed = storage.getJSON("emcom_session");
    if (parsed) {
      setSession(parsed);
      setFormData(prev => ({ ...prev, seller_name: parsed.seller_name || "" }));
    } else {
      navigate("/login");
    }
    
    // Load calculation from history if available
    try {
      const editData = localStorage.getItem("emcom_edit_calculation");
      if (editData) {
        const { type, data } = JSON.parse(editData);
        if (type === "vo") {
          setIsEditMode(true);
          // Pre-fill form with saved data
          setFormData(prev => ({
            ...prev,
            vehicle_description: data.vehicle_description || "",
            purchase_price: data.purchase_price_ht?.toString() || "",
            reconditioning_cost: data.reconditioning_cost_ht?.toString() || "",
            selling_price_ttc: data.selling_price_ttc?.toString() || "",
            has_financing: data.has_financing || false,
            has_delivery: data.has_delivery || false,
            waxyol_type: data.waxyol_type || "NON",
            has_warranty: data.has_warranty || false,
            accessories_amount: data.accessories_amount?.toString() || "",
            seller_name: data.seller_name || "",
          }));
          // Set result to show previous calculation
          setResult(data);
          toast.info("Calcul chargé depuis l'historique - Modifiez les valeurs et recalculez");
        }
        // Clear the edit data after loading
        localStorage.removeItem("emcom_edit_calculation");
      }
    } catch (e) {
      console.error("Error loading edit data:", e);
      localStorage.removeItem("emcom_edit_calculation");
    }
  }, [navigate]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(value || 0);
  };

  const formatPercent = (value) => {
    return `${(value || 0).toFixed(2)} %`;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setResult(null);
  };

  const handleCalculate = async () => {
    // Validation
    if (!formData.vehicle_description.trim()) {
      toast.error("Veuillez entrer la description du véhicule");
      return;
    }
    if (!formData.purchase_price_ttc || parseFloat(formData.purchase_price_ttc) <= 0) {
      toast.error("Veuillez entrer le prix d'achat");
      return;
    }
    if (!formData.selling_price_ttc || parseFloat(formData.selling_price_ttc) <= 0) {
      toast.error("Veuillez entrer le prix de vente");
      return;
    }
    if (!formData.seller_name.trim()) {
      toast.error("Veuillez entrer le nom du vendeur");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        vehicle_description: formData.vehicle_description,
        has_vat: formData.has_vat,
        purchase_price_ttc: parseFloat(formData.purchase_price_ttc) || 0,
        reconditioning_cost: parseFloat(formData.reconditioning_cost) || 0,
        selling_price_ttc: parseFloat(formData.selling_price_ttc) || 0,
        has_financing: formData.has_financing,
        has_delivery: formData.has_delivery,
        waxyol_type: formData.waxyol_type,
        has_warranty: formData.has_warranty,
        accessories_amount: parseFloat(formData.accessories_amount) || 0,
        seller_name: formData.seller_name,
        concession_id: session?.concession_id || "",
      };

      const response = await axios.post(`${API}/vo/calculate`, payload);
      setResult(response.data);
      toast.success("Calcul effectué !");
    } catch (error) {
      console.error("Calculation error:", error);
      toast.error(error.response?.data?.detail || "Erreur lors du calcul");
    }
    setLoading(false);
  };

  const handlePrint = async () => {
    if (!result) return;
    
    setPrinting(true);
    try {
      const response = await axios.post(`${API}/vo/pdf/export`, result, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF généré !");
    } catch (error) {
      console.error("Print error:", error);
      toast.error("Erreur lors de la génération du PDF");
    }
    setPrinting(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-gradient-to-r from-red-600 to-red-700 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => navigate("/")}
                className="text-white hover:bg-white/10"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white logo-container flex items-center justify-center shadow-md">
                  <img src={logoMcom} alt="Verbaere" className="w-8 h-8 object-contain" />
                </div>
                <div>
                  <h1 className="text-xl font-bold">Véhicule d'Occasion</h1>
                  <p className="text-xs text-white/70">e-MCom by Verbaere Performance Suite</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Link 
                to="/history"
                className="flex items-center gap-2 px-3 py-2 rounded-md bg-white/10 hover:bg-white/20 transition-colors"
                data-testid="vo-history-link"
              >
                <History className="w-4 h-4" />
                <span className="text-sm hidden sm:inline">Historique</span>
              </Link>
              <div className="text-right">
                <p className="text-sm text-white/80">Vendeur</p>
                <p className="font-medium">{formData.seller_name || session?.seller_name || "-"}</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        
        {/* Formulaire */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          
          {/* Colonne Gauche - Informations véhicule */}
          <div className="space-y-6">
            {/* Vendeur */}
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-500">Vendeur</CardTitle>
              </CardHeader>
              <CardContent>
                <Input
                  value={formData.seller_name}
                  onChange={(e) => handleInputChange("seller_name", e.target.value)}
                  placeholder="Nom du vendeur"
                  data-testid="vo-seller-name"
                />
              </CardContent>
            </Card>

            {/* Description véhicule */}
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-500">Description du véhicule</CardTitle>
              </CardHeader>
              <CardContent>
                <Input
                  value={formData.vehicle_description}
                  onChange={(e) => handleInputChange("vehicle_description", e.target.value)}
                  placeholder="Ex: Peugeot 308 1.6 HDi 120ch Active - 2021 - 45 000 km"
                  data-testid="vo-vehicle-description"
                />
              </CardContent>
            </Card>

            {/* Prix */}
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
                  <Euro className="w-4 h-4" />
                  Prix du véhicule
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Sélecteur TVA */}
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <Label className="text-sm font-medium text-blue-700 dark:text-blue-300">Type de TVA</Label>
                  <div className="flex gap-2 mt-2">
                    <Button
                      type="button"
                      variant={formData.has_vat ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleInputChange("has_vat", true)}
                      className={formData.has_vat ? "bg-blue-600 hover:bg-blue-700" : ""}
                      data-testid="vo-with-vat-btn"
                    >
                      Avec TVA
                    </Button>
                    <Button
                      type="button"
                      variant={!formData.has_vat ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleInputChange("has_vat", false)}
                      className={!formData.has_vat ? "bg-amber-600 hover:bg-amber-700" : ""}
                      data-testid="vo-without-vat-btn"
                    >
                      Sans TVA (marge)
                    </Button>
                  </div>
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                    {formData.has_vat 
                      ? "TVA sur le prix total du véhicule" 
                      : "TVA uniquement sur la marge générée"}
                  </p>
                </div>

                <div>
                  <Label className="text-sm">
                    {formData.has_vat ? "Prix d'achat TTC" : "Prix d'achat (sans TVA)"}
                  </Label>
                  <Input
                    type="number"
                    value={formData.purchase_price_ttc}
                    onChange={(e) => handleInputChange("purchase_price_ttc", e.target.value)}
                    placeholder="0"
                    className="mt-1"
                    data-testid="vo-purchase-price"
                  />
                </div>
                <div>
                  <Label className="text-sm">Frais de remise en état HT</Label>
                  <Input
                    type="number"
                    value={formData.reconditioning_cost}
                    onChange={(e) => handleInputChange("reconditioning_cost", e.target.value)}
                    placeholder="0"
                    className="mt-1"
                    data-testid="vo-reconditioning-cost"
                  />
                </div>
                <div className="pt-2 border-t">
                  <Label className="text-sm font-medium">
                    {formData.has_vat ? "Prix de vente client TTC" : "Prix de vente client"}
                  </Label>
                  <Input
                    type="number"
                    value={formData.selling_price_ttc}
                    onChange={(e) => handleInputChange("selling_price_ttc", e.target.value)}
                    placeholder="0"
                    className="mt-1 text-lg font-bold"
                    data-testid="vo-selling-price"
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Colonne Droite - Commissions */}
          <div className="space-y-6">
            {/* Options de commission */}
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
                  <Percent className="w-4 h-4" />
                  Commission Vendeur VO
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Financement - 125€ avec, 75€ sans */}
                <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div>
                    <Label className="font-medium">Financement</Label>
                    <p className="text-xs text-gray-500">
                      {formData.has_financing ? "Avec financement: 125 €" : "Sans financement: 75 €"}
                    </p>
                  </div>
                  <Switch
                    checked={formData.has_financing}
                    onCheckedChange={(checked) => handleInputChange("has_financing", checked)}
                    data-testid="vo-financing-switch"
                  />
                </div>

                {/* Forfait livraison */}
                <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div>
                    <Label className="font-medium">Forfait livraison</Label>
                    <p className="text-xs text-gray-500">45 €</p>
                  </div>
                  <Switch
                    checked={formData.has_delivery}
                    onCheckedChange={(checked) => handleInputChange("has_delivery", checked)}
                    data-testid="vo-delivery-switch"
                  />
                </div>

                {/* Waxyol - Boutons comme VN */}
                <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <Label className="font-medium mb-2 block">Waxyol</Label>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      type="button"
                      onClick={() => handleInputChange("waxyol_type", "NON")}
                      className={`p-3 rounded-lg border-2 transition-all text-center ${
                        formData.waxyol_type === "NON"
                          ? "border-red-500 bg-red-50 dark:bg-red-900/20"
                          : "border-gray-200 dark:border-gray-700 hover:border-gray-300"
                      }`}
                      data-testid="vo-waxyol-non"
                    >
                      <span className={`font-medium ${formData.waxyol_type === "NON" ? "text-red-600" : ""}`}>
                        Aucun
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleInputChange("waxyol_type", "BASIQUE")}
                      className={`p-3 rounded-lg border-2 transition-all text-center ${
                        formData.waxyol_type === "BASIQUE"
                          ? "border-red-500 bg-red-50 dark:bg-red-900/20"
                          : "border-gray-200 dark:border-gray-700 hover:border-gray-300"
                      }`}
                      data-testid="vo-waxyol-basique"
                    >
                      <span className={`font-medium ${formData.waxyol_type === "BASIQUE" ? "text-red-600" : ""}`}>
                        Basique
                      </span>
                      <p className="text-xs text-gray-500">20 €</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleInputChange("waxyol_type", "CERAMIQUE")}
                      className={`p-3 rounded-lg border-2 transition-all text-center ${
                        formData.waxyol_type === "CERAMIQUE"
                          ? "border-red-500 bg-red-50 dark:bg-red-900/20"
                          : "border-gray-200 dark:border-gray-700 hover:border-gray-300"
                      }`}
                      data-testid="vo-waxyol-ceramique"
                    >
                      <span className={`font-medium ${formData.waxyol_type === "CERAMIQUE" ? "text-red-600" : ""}`}>
                        Céramique
                      </span>
                      <p className="text-xs text-gray-500">40 €</p>
                    </button>
                  </div>
                </div>

                {/* Extension garantie */}
                <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div>
                    <Label className="font-medium">Extension garantie</Label>
                    <p className="text-xs text-gray-500">20 €</p>
                  </div>
                  <Switch
                    checked={formData.has_warranty}
                    onCheckedChange={(checked) => handleInputChange("has_warranty", checked)}
                    data-testid="vo-warranty-switch"
                  />
                </div>

                {/* Accessoires */}
                <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <Label className="font-medium">Accessoires TTC</Label>
                  <p className="text-xs text-gray-500 mb-2">Commission: 5% du montant</p>
                  <Input
                    type="number"
                    value={formData.accessories_amount}
                    onChange={(e) => handleInputChange("accessories_amount", e.target.value)}
                    placeholder="0"
                    data-testid="vo-accessories"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Bouton Calculer */}
            <Button
              onClick={handleCalculate}
              disabled={loading}
              className="w-full h-14 text-lg bg-red-600 hover:bg-red-700"
              data-testid="vo-calculate-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <Calculator className="w-5 h-5 mr-2" />
              )}
              Calculer la marge
            </Button>
          </div>
        </div>

        {/* Résultat */}
        {result && (
          <Card className="border-0 shadow-lg">
            <CardHeader className="bg-gray-900 text-white rounded-t-lg">
              <CardTitle className="flex items-center justify-between">
                <span>Résultat du calcul</span>
                <Button
                  onClick={handlePrint}
                  disabled={printing}
                  variant="outline"
                  className="bg-white text-gray-900 hover:bg-gray-100"
                  data-testid="vo-print-btn"
                >
                  {printing ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Printer className="w-4 h-4 mr-2" />
                  )}
                  Imprimer
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              {/* Véhicule */}
              <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                <p className="text-lg font-bold text-red-800 dark:text-red-300">{result.vehicle_description}</p>
              </div>

              {/* Résumé prix */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-lg text-center">
                  <p className="text-xs text-gray-500">Prix d'achat HT</p>
                  <p className="text-lg font-bold">{formatCurrency(result.purchase_price_ht)}</p>
                </div>
                <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-lg text-center">
                  <p className="text-xs text-gray-500">Frais remise état</p>
                  <p className="text-lg font-bold">{formatCurrency(result.reconditioning_cost_ht)}</p>
                </div>
                <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-lg text-center">
                  <p className="text-xs text-gray-500">Coût total HT</p>
                  <p className="text-lg font-bold">{formatCurrency(result.total_cost_ht)}</p>
                </div>
                <div className="p-4 bg-blue-100 dark:bg-blue-900/20 rounded-lg text-center">
                  <p className="text-xs text-gray-500">Prix vente TTC</p>
                  <p className="text-lg font-bold text-blue-600">{formatCurrency(result.selling_price_ttc)}</p>
                </div>
              </div>

              {/* Marge */}
              <div className={`p-4 rounded-lg ${result.margin_ht >= 0 ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Marge brute HT</p>
                    <p className={`text-2xl font-bold ${result.margin_ht >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {result.margin_ht >= 0 ? '+' : ''}{formatCurrency(result.margin_ht)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600 dark:text-gray-400">% Marge</p>
                    <p className={`text-xl font-bold ${result.margin_ht >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatPercent(result.margin_percentage)}
                    </p>
                  </div>
                </div>
                {result.is_below_min_margin && (
                  <div className="mt-3 p-2 bg-orange-100 dark:bg-orange-900/30 rounded flex items-center gap-2 text-orange-700 dark:text-orange-400">
                    <AlertTriangle className="w-4 h-4" />
                    <span className="text-sm">Marge inférieure à 3% - Commission forfaitaire 40€</span>
                  </div>
                )}
              </div>

              {/* Commissions */}
              <div className="space-y-2">
                <h3 className="font-medium text-gray-700 dark:text-gray-300">Détail commissions</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {/* Commission vente avec/sans financement - toujours affichée */}
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {result.has_financing ? "Commission avec financement" : "Commission sans financement"}
                    </p>
                    <p className="font-bold text-blue-600 dark:text-blue-400">{formatCurrency(result.sale_commission)}</p>
                  </div>
                  {result.delivery_commission > 0 && (
                    <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <p className="text-xs text-gray-500 dark:text-gray-400">Forfait livraison</p>
                      <p className="font-bold text-blue-600 dark:text-blue-400">{formatCurrency(result.delivery_commission)}</p>
                    </div>
                  )}
                  {result.waxyol_commission > 0 && (
                    <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Waxyol {result.waxyol_type === "CERAMIQUE" ? "Céramique" : "Basique"}
                      </p>
                      <p className="font-bold text-blue-600 dark:text-blue-400">{formatCurrency(result.waxyol_commission)}</p>
                    </div>
                  )}
                  {result.warranty_commission > 0 && (
                    <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <p className="text-xs text-gray-500 dark:text-gray-400">Extension garantie</p>
                      <p className="font-bold text-blue-600 dark:text-blue-400">{formatCurrency(result.warranty_commission)}</p>
                    </div>
                  )}
                  {result.accessories_commission > 0 && (
                    <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <p className="text-xs text-gray-500 dark:text-gray-400">Accessoires</p>
                      <p className="font-bold text-blue-600 dark:text-blue-400">{formatCurrency(result.accessories_commission)}</p>
                    </div>
                  )}
                </div>
                <div className="p-3 bg-blue-600 text-white rounded-lg flex justify-between items-center">
                  <span className="font-medium">Total Commission</span>
                  <span className="text-xl font-bold">{formatCurrency(result.total_commission)}</span>
                </div>
              </div>

              {/* Résultat final */}
              <div 
                className={`p-6 rounded-lg ${result.margin_after_commission >= 0 ? 'bg-green-600' : 'bg-red-600'} text-white`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {result.margin_after_commission >= 0 ? (
                      <Check className="w-8 h-8" />
                    ) : (
                      <AlertTriangle className="w-8 h-8" />
                    )}
                    <div>
                      <p className="text-sm opacity-80">Marge après commission</p>
                      <p className="text-3xl font-bold">
                        {result.margin_after_commission >= 0 ? '+' : ''}{formatCurrency(result.margin_after_commission)}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm opacity-80">% du prix vente</p>
                    <p className="text-2xl font-bold">
                      {result.margin_after_commission_percent >= 0 ? '+' : ''}{formatPercent(result.margin_after_commission_percent)}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
